import { SORT_OPTIONS, sortOptionFromParam, sortOptionToParam } from "../components/SortDropdown";
import type { SortOption } from "../types";

export const POCA_URL_GROUP = "group";
export const POCA_URL_MEMBER = "member";
export const POCA_URL_SEARCH = "q";
export const POCA_URL_MIN_PRICE = "min";
export const POCA_URL_MAX_PRICE = "max";
export const POCA_URL_SORT = "sort";
export const POCA_URL_TAGS = "tags";

export type PocaUrlFilters = {
  groupHandle: string | null;
  member: string | null;
  search: string;
  minPrice: number | null;
  maxPrice: number | null;
  sort: SortOption;
  tags: string[];
};

function parseTagsParam(raw: string | null): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function parsePriceParam(raw: string | null): number | null {
  if (!raw?.trim()) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function readPocaUrlFilters(
  searchString = window.location.search
): PocaUrlFilters {
  const params = new URLSearchParams(searchString);
  const groupHandle = params.get(POCA_URL_GROUP)?.trim() || null;
  const member = params.get(POCA_URL_MEMBER)?.trim() || null;
  const search = params.get(POCA_URL_SEARCH)?.trim() ?? "";
  const minPrice = parsePriceParam(params.get(POCA_URL_MIN_PRICE));
  const maxPrice = parsePriceParam(params.get(POCA_URL_MAX_PRICE));
  const sort =
    sortOptionFromParam(params.get(POCA_URL_SORT)) ?? SORT_OPTIONS[0];
  const tags = parseTagsParam(params.get(POCA_URL_TAGS));

  return {
    groupHandle,
    member: groupHandle ? member : null,
    search,
    minPrice,
    maxPrice,
    sort,
    // 종류(TYPE) 태그는 그룹 안에서만 쓰는 필터 — 그룹 없는 URL 의 tags 는 버린다
    tags: groupHandle ? tags : [],
  };
}

export type PocaUrlWriteParams = {
  groupHandle: string | null;
  member: string | null;
  search: string;
  minPrice: number | null;
  maxPrice: number | null;
  sort: SortOption;
  tags: string[];
};

export function writePocaUrlFilters({
  groupHandle,
  member,
  search,
  minPrice,
  maxPrice,
  sort,
  tags,
}: PocaUrlWriteParams): void {
  const url = new URL(window.location.href);
  const defaultSort = SORT_OPTIONS[0];

  if (groupHandle) {
    url.searchParams.set(POCA_URL_GROUP, groupHandle);
  } else {
    url.searchParams.delete(POCA_URL_GROUP);
  }

  if (member && groupHandle) {
    url.searchParams.set(POCA_URL_MEMBER, member);
  } else {
    url.searchParams.delete(POCA_URL_MEMBER);
  }

  const trimmedSearch = search.trim();
  if (trimmedSearch) {
    url.searchParams.set(POCA_URL_SEARCH, trimmedSearch);
  } else {
    url.searchParams.delete(POCA_URL_SEARCH);
  }

  if (minPrice != null) {
    url.searchParams.set(POCA_URL_MIN_PRICE, String(minPrice));
  } else {
    url.searchParams.delete(POCA_URL_MIN_PRICE);
  }

  if (maxPrice != null) {
    url.searchParams.set(POCA_URL_MAX_PRICE, String(maxPrice));
  } else {
    url.searchParams.delete(POCA_URL_MAX_PRICE);
  }

  if (sortOptionToParam(sort) !== sortOptionToParam(defaultSort)) {
    url.searchParams.set(POCA_URL_SORT, sortOptionToParam(sort));
  } else {
    url.searchParams.delete(POCA_URL_SORT);
  }

  if (tags.length > 0 && groupHandle) {
    url.searchParams.set(POCA_URL_TAGS, tags.join(","));
  } else {
    url.searchParams.delete(POCA_URL_TAGS);
  }

  const next = `${url.pathname}${url.search}${url.hash}`;
  if (
    next ===
    `${window.location.pathname}${window.location.search}${window.location.hash}`
  ) {
    return;
  }

  window.history.replaceState(window.history.state, "", next);
}
